import type { ButtonHTMLAttributes } from 'react';
import { cn } from './cn.ts';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent-fill text-on-accent hover:bg-accent-fill/90 disabled:bg-accent-fill/50',
  secondary: 'border border-border bg-surface text-text hover:bg-hover disabled:text-muted',
  ghost: 'text-muted hover:bg-hover hover:text-text',
  // Destructive when it can act, and faded when it cannot. Opacity rather than a colour override: a
  // `disabled:` colour has to win against the variant's own colour, and when it does it wins everywhere —
  // measured on an *enabled* button and found muted, which is how a greyed-out red hides in plain sight.
  danger:
    'border border-danger/40 text-danger hover:bg-danger/10 disabled:opacity-40 disabled:hover:bg-transparent',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  className,
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'md' }) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded font-medium whitespace-nowrap transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed',
        size === 'sm' ? 'h-7 px-2 text-xs' : 'h-8 px-3 text-sm',
        VARIANTS[variant],
        className,
      )}
      {...props}
    />
  );
}
